import { useState, useEffect } from "react";
import { useDocHubDialog, DocumentDialogData } from "@/contexts/DocHubDialogContext";
import { useBrandSelection } from "@/hooks/useBrandSelection";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { DocumentationTagSelector } from "@/components/documentation-hub/DocumentationTagSelector";

interface DocHubCategory {
  id: string;
  brand_id: string;
  name: string;
  description?: string | null;
  color?: string;
}

export const DocumentDialog = () => {
  const { currentBrandId } = useBrandSelection();
  const queryClient = useQueryClient();
  const { dialogType, isOpen, documentData, closeDialog, onSuccess } = useDocHubDialog();
  
  const [formData, setFormData] = useState<DocumentDialogData>({
    name: '',
    link: null,
    category_id: null,
    tags: [],
    images: [],
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const isEditing = !!documentData?.id;
  const dialogOpen = dialogType === 'document' && isOpen;

  // Fetch categories
  const { data: categories = [] } = useQuery<DocHubCategory[]>({
    queryKey: ['doc-hub-categories', currentBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_doc_hub_files_categories')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('name');
      if (error) throw error;
      return data as DocHubCategory[];
    },
    enabled: !!currentBrandId && dialogOpen,
  });

  // Sync form data when dialog opens
  useEffect(() => {
    if (documentData && dialogOpen) {
      setFormData({
        id: documentData.id,
        name: documentData.name || '',
        link: documentData.link || null,
        category_id: documentData.category_id || null,
        tags: documentData.tags || [],
        images: documentData.images || [],
      });
    }
  }, [documentData, dialogOpen]);

  // Create/Update file mutation
  const fileMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Handle file uploads
      if (uploadedFiles.length > 0) {
        const uploadedUrls: string[] = [];
        await Promise.all(
          uploadedFiles.map(async (file) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${currentBrandId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
            const { error: uploadError } = await supabase.storage
              .from('os_doc_hub_files')
              .upload(fileName, file);
            if (uploadError) throw uploadError;
            const { data: { publicUrl } } = supabase.storage
              .from('os_doc_hub_files')
              .getPublicUrl(fileName);
            uploadedUrls.push(publicUrl);
          })
        );

        if (isEditing && formData.id) {
          // Get existing images
          const { data: existingFile } = await supabase
            .from('os_doc_hub_files')
            .select('images')
            .eq('id', formData.id)
            .single();
          
          const existingImages = Array.isArray(existingFile?.images) ? existingFile.images : [];
          const allImages = [...existingImages, ...uploadedUrls];
          
          const { error: updateError } = await supabase
            .from('os_doc_hub_files')
            .update({
              name: formData.name,
              category_id: formData.category_id || null,
              link: formData.link || null,
              images: allImages,
              tags: JSON.stringify(formData.tags || []),
            })
            .eq('id', formData.id);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from('os_doc_hub_files')
            .insert({
              brand_id: currentBrandId,
              user_id: user.id,
              name: formData.name || 'Untitled Document',
              category_id: formData.category_id || null,
              link: formData.link || null,
              images: uploadedUrls,
              tags: JSON.stringify(formData.tags || []),
            });
          if (insertError) throw insertError;
        }
        return;
      }
      
      // Handle regular file update/create
      if (isEditing && formData.id) {
        const { error } = await supabase
          .from('os_doc_hub_files')
          .update({
            name: formData.name,
            category_id: formData.category_id,
            link: formData.link,
            tags: JSON.stringify(formData.tags || []),
          })
          .eq('id', formData.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('os_doc_hub_files')
          .insert({
            brand_id: currentBrandId,
            user_id: user.id,
            name: formData.name,
            category_id: formData.category_id,
            link: formData.link,
            tags: JSON.stringify(formData.tags || []),
          });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['os-doc-hub-files'] });
      toast.success(isEditing ? 'Document updated' : 'Document created');
      onSuccess?.();
      handleClose(false);
    },
    onError: (error) => {
      toast.error('Failed to save document: ' + error.message);
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setUploadedFiles(prev => [...prev, ...acceptedFiles]);
    },
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/*': ['.txt', '.md'],
    },
  });

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!currentBrandId || !formData.name.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    fileMutation.mutate();
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      closeDialog();
      setFormData({ name: '', link: null, category_id: null, tags: [], images: [] });
      setUploadedFiles([]);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleClose} modal={false}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isEditing ? "Edit Document" : "Add New Document"} 
            popupContext="/documentation-hub" 
            isOpen={dialogOpen}
          >
            {isEditing ? 'Edit Document' : 'Add New Document'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Document Name *</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              placeholder="Enter document name" 
            />
          </div>
          <div>
            <Label htmlFor="category">Category</Label>
            <Select 
              value={formData.category_id || 'none'} 
              onValueChange={(value) => setFormData({ ...formData, category_id: value === 'none' ? null : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="none">No Category</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <DocumentationTagSelector
              brandId={currentBrandId || ''}
              selectedTagIds={formData.tags}
              onTagsChange={(tagIds) => setFormData({ ...formData, tags: tagIds })}
            />
          </div>
          <div>
            <Label htmlFor="link">External Link (optional)</Label>
            <Input 
              id="link" 
              value={formData.link || ''} 
              onChange={(e) => setFormData({ ...formData, link: e.target.value || null })} 
              placeholder="https://..." 
            />
          </div>
          <div>
            <Label>Upload Files</Label>
            <div 
              {...getRootProps()} 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
              </p>
            </div>
            {uploadedFiles.length > 0 && (
              <div className="mt-2 space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-secondary rounded">
                    <span className="text-sm truncate flex-1">{file.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeFile(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={fileMutation.isPending}>
              {isEditing ? 'Update' : 'Create'} Document
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
